import React, {
	useState,
	useMemo,
	useRef,
	useEffect,
	useCallback,
} from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { Button } from '../../../buttons/Button';
import { BodyM, BodyMSemiBold, BodyS } from '../../../typography';
import {
	LoadingAnimation,
	LoadingAnimationSize,
} from '../../../loader/LoadingAnimation';
import { SearchBox } from '../../../search-box/SearchBox';
import { Skeleton } from '../../../skeleton/Skeleton';
import { FixedSizeList } from 'react-window';
import type { ListChildComponentProps } from 'react-window';
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
} from '../../../popover/Popover';
import type {
	IDropdownItem,
	ISingleSelectDropdownProps,
} from '../../SelectDropdown';

type ISearchClearableSelectDropdownProps<T extends IDropdownItem> =
	ISingleSelectDropdownProps<T> & {
		loadingSpinner: React.ReactNode;
	};

enum ContentTypeToRender {
	Loading,
	Empty,
	NoMatches,
	Ready,
}

interface ISearchClearableVirtualListRowData<T extends IDropdownItem> {
	displayedItems: T[];
	selectedItem: T | null;
	setTestId: (elementName: string) => string;
	onPick: (item: T) => void;
}

const makeDataTestId = (scope: string) => (elementName: string) =>
	`${scope}-${elementName}`;

function collapseAccidentalFullTextSelection(input: HTMLInputElement) {
	if (document.activeElement !== input) {
		return;
	}

	const { selectionStart, selectionEnd, value } = input;
	const isFullTextSelected =
		value.length > 0 && selectionStart === 0 && selectionEnd === value.length;

	if (!isFullTextSelected) {
		return;
	}

	const caretEndOffset = value.length;

	try {
		input.setSelectionRange(caretEndOffset, caretEndOffset);
	} catch {
		/* setSelectionRange can throw for invalid types/state. */
	}
}

function SearchClearableVirtualListRow<T extends IDropdownItem>({
	index,
	style,
	data,
}: ListChildComponentProps<ISearchClearableVirtualListRowData<T>>) {
	const item = data.displayedItems[index];
	if (!item) {
		return <div style={style} />;
	}

	const isSelected = item.id === data.selectedItem?.id;
	const OptionLabel = isSelected ? BodyMSemiBold : BodyM;

	return (
		<div style={style} className="w-full min-w-0 overflow-hidden">
			<button
				type="button"
				role="option"
				aria-selected={isSelected}
				onMouseDown={(event) => event.preventDefault()}
				onClick={() => data.onPick(item)}
				className="flex h-full w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-s px-12 py-10 text-left text-14 outline-none hover:bg-interactive-light hover:text-text-primary"
				data-testid={data.setTestId('dropdown-list-item')}
			>
				<OptionLabel className="min-w-0 w-full">
					<span className="inline-flex max-w-full min-w-0 items-center gap-8">
						<span
							className="min-w-0 truncate"
							data-testid={data.setTestId('dropdown-list-item-label')}
						>
							{item.name}
						</span>
						{isSelected ? (
							<span className="inline-flex shrink-0 items-center">
								<Icon name="check" size="xs" />
							</span>
						) : null}
					</span>
				</OptionLabel>
			</button>
		</div>
	);
}

function getContentTypeToRender(
	isLoadingOptions: boolean | undefined,
	itemCount: number,
	displayedItemCount: number
): ContentTypeToRender {
	if (isLoadingOptions) {
		return ContentTypeToRender.Loading;
	}

	if (!itemCount) {
		return ContentTypeToRender.Empty;
	}

	if (!displayedItemCount) {
		return ContentTypeToRender.NoMatches;
	}

	return ContentTypeToRender.Ready;
}

function getMenuContentKind(
	isLoadingOptions: boolean | undefined,
	isServerSearch: boolean,
	itemCount: number,
	displayedItemCount: number
): ContentTypeToRender {
	const isLoadingSpinnerVisible =
		Boolean(isLoadingOptions) && (!isServerSearch || itemCount === 0);

	return getContentTypeToRender(
		isLoadingSpinnerVisible,
		itemCount,
		displayedItemCount
	);
}

function preventPopoverCloseFromAnchor(
	event: { target: EventTarget | null; preventDefault: () => void },
	anchor: HTMLElement | null
): void {
	if (event.target instanceof Node && anchor?.contains(event.target)) {
		event.preventDefault();
	}
}

function getVirtualListHeight(
	isVirtualized: boolean,
	itemCount: number,
	virtualListMaxHeight: number,
	virtualListRowHeight: number
): number {
	if (!isVirtualized || itemCount === 0) {
		return 0;
	}

	return Math.min(virtualListMaxHeight, itemCount * virtualListRowHeight);
}

function filterItemsByQuery<T extends IDropdownItem>(
	items: T[],
	filterQuery: string
): T[] {
	const query = filterQuery.trim().toLowerCase();
	if (!query) {
		return items;
	}

	return items.filter((item) => item.name.toLowerCase().includes(query));
}

function SearchClearablePlainItemList<T extends IDropdownItem>({
	items,
	selectedItem,
	onPick,
	setTestId,
}: Readonly<{
	items: T[];
	selectedItem: T | null;
	onPick: (item: T) => void;
	setTestId: (elementName: string) => string;
}>) {
	return items.map((item) => {
		const isSelected = item.id === selectedItem?.id;
		const OptionLabel = isSelected ? BodyMSemiBold : BodyM;

		return (
			<button
				key={item.id}
				type="button"
				role="option"
				aria-selected={isSelected}
				onMouseDown={(event) => event.preventDefault()}
				onClick={() => onPick(item)}
				className="flex w-full cursor-pointer items-center gap-2 px-12 py-10 text-left text-14 outline-none hover:bg-interactive-light hover:text-text-primary"
				data-testid={setTestId('dropdown-list-item')}
			>
				<OptionLabel className="min-w-0 w-full">
					<span className="inline-flex max-w-full min-w-0 items-center gap-8">
						<span
							className="min-w-0 truncate"
							data-testid={setTestId('dropdown-list-item-label')}
						>
							{item.name}
						</span>
						{isSelected ? (
							<span className="inline-flex shrink-0 items-center">
								<Icon name="check" size="xs" />
							</span>
						) : null}
					</span>
				</OptionLabel>
			</button>
		);
	});
}

function SearchClearableMenuBody<T extends IDropdownItem>({
	menuContentKind,
	isVirtualized,
	displayedItems,
	selectedItem,
	onPick,
	setTestId,
	virtualListHeight,
	virtualListRowHeight,
	virtualListItemData,
}: Readonly<{
	menuContentKind: ContentTypeToRender;
	isVirtualized: boolean;
	displayedItems: T[];
	selectedItem: T | null;
	onPick: (item: T) => void;
	setTestId: (elementName: string) => string;
	virtualListHeight: number;
	virtualListRowHeight: number;
	virtualListItemData: ISearchClearableVirtualListRowData<T>;
}>) {
	return (
		<>
			{menuContentKind === ContentTypeToRender.Loading && (
				<div className="flex w-full justify-center gap-12 py-12">
					<LoadingAnimation size={LoadingAnimationSize.Small} />
					<BodyS className="text-text-secondary">Loading options</BodyS>
				</div>
			)}

			{menuContentKind === ContentTypeToRender.Empty && (
				<div className="flex justify-center p-10">
					<BodyM className="text-zinc-500">No data</BodyM>
				</div>
			)}

			{menuContentKind === ContentTypeToRender.NoMatches && (
				<div className="flex justify-center p-10">
					<BodyM className="text-zinc-500">No matching options</BodyM>
				</div>
			)}

			{menuContentKind === ContentTypeToRender.Ready && isVirtualized && (
				<FixedSizeList<ISearchClearableVirtualListRowData<T>>
					height={virtualListHeight}
					width="100%"
					itemCount={displayedItems.length}
					itemSize={virtualListRowHeight}
					itemData={virtualListItemData}
				>
					{SearchClearableVirtualListRow}
				</FixedSizeList>
			)}

			{menuContentKind === ContentTypeToRender.Ready && !isVirtualized && (
				<SearchClearablePlainItemList
					items={displayedItems}
					selectedItem={selectedItem}
					onPick={onPick}
					setTestId={setTestId}
				/>
			)}
		</>
	);
}

function useSearchClearableOpenState(
	isDisabledByLoading: boolean,
	isServerSearch: boolean,
	onOpen: ((close: () => void) => void) | undefined,
	selectedItemName: string | undefined
) {
	const [isOpen, setOpen] = useState<boolean>(false);
	const [filterQuery, setFilterQuery] = useState<string>('');

	const handleOpenChange = useCallback(
		(isMenuOpen: boolean) => {
			if (isMenuOpen && isDisabledByLoading) {
				return;
			}

			if (isMenuOpen && onOpen) {
				onOpen(() => setOpen(false));
			}

			// Server search re-queries by the input value, so opening must not
			// prefill the selected name (it would immediately filter the list).
			if (isMenuOpen && isServerSearch) {
				setFilterQuery('');
			} else {
				setFilterQuery(isMenuOpen ? (selectedItemName ?? '') : '');
			}
			setOpen(isMenuOpen);
		},
		[isDisabledByLoading, isServerSearch, onOpen, selectedItemName]
	);

	useEffect(() => {
		if (isDisabledByLoading && isOpen) {
			setOpen(false);
		}
	}, [isDisabledByLoading, isOpen]);

	return { isOpen, filterQuery, setFilterQuery, handleOpenChange };
}

function useDebouncedSearchTerm(
	filterQuery: string,
	isServerSearch: boolean,
	searchDebounceMs: number,
	onSearchTermChange: ((searchTerm: string) => void) | undefined
) {
	const onSearchTermChangeRef = useRef(onSearchTermChange);
	onSearchTermChangeRef.current = onSearchTermChange;

	useEffect(() => {
		if (!isServerSearch) {
			return;
		}

		const timeoutId = globalThis.setTimeout(() => {
			onSearchTermChangeRef.current?.(filterQuery.trim());
		}, searchDebounceMs);

		return () => globalThis.clearTimeout(timeoutId);
	}, [filterQuery, isServerSearch, searchDebounceMs]);
}

function SearchClearableSearchInput({
	isOpen,
	filterQuery,
	selectedItemName,
	placeholder,
	hasGreyPlaceholder,
	listPanelId,
	onOpenMenu,
	onFilterQueryChange,
}: Readonly<{
	isOpen: boolean;
	filterQuery: string;
	selectedItemName: string | undefined;
	placeholder: string;
	hasGreyPlaceholder: boolean;
	listPanelId: string;
	onOpenMenu: () => void;
	onFilterQueryChange: (value: string) => void;
}>) {
	const handleChange = (value: string) => {
		onOpenMenu();
		onFilterQueryChange(value);
	};

	return (
		<SearchBox
			debounceMs={0}
			view="plain"
			value={isOpen ? filterQuery : (selectedItemName ?? '')}
			placeholder={placeholder}
			className={cn(
				'min-w-0 truncate',
				hasGreyPlaceholder && 'placeholder:text-zinc-400'
			)}
			autoComplete="off"
			spellCheck={false}
			role="combobox"
			aria-expanded={isOpen}
			aria-controls={isOpen ? listPanelId : undefined}
			onPointerDown={(event) => {
				event.stopPropagation();
				onOpenMenu();
			}}
			onFocus={(event) => {
				onOpenMenu();
				globalThis.requestAnimationFrame(() => {
					collapseAccidentalFullTextSelection(event.currentTarget);
				});
			}}
			onMouseUp={(event) => {
				globalThis.requestAnimationFrame(() => {
					collapseAccidentalFullTextSelection(event.currentTarget);
				});
			}}
			onChange={handleChange}
		/>
	);
}

function SearchClearableTriggerContent({
	isDisabledByLoading,
	isSpinnerLoading,
	isClearControlVisible,
	leadingIcon,
	loadingSpinner,
	setTestId,
	...inputProps
}: Readonly<{
	isDisabledByLoading: boolean;
	isSpinnerLoading: boolean;
	isClearControlVisible: boolean;
	leadingIcon: React.ReactNode;
	loadingSpinner: React.ReactNode;
	isOpen: boolean;
	filterQuery: string;
	selectedItemName: string | undefined;
	placeholder: string;
	hasGreyPlaceholder: boolean;
	listPanelId: string;
	setTestId: (elementName: string) => string;
	onOpenMenu: () => void;
	onFilterQueryChange: (value: string) => void;
}>) {
	if (isSpinnerLoading) {
		return loadingSpinner;
	}

	return (
		<>
			{leadingIcon ? (
				<span
					className={cn(
						'inline-flex shrink-0 items-center',
						isDisabledByLoading && 'opacity-50'
					)}
					data-testid={setTestId('leading-icon')}
				>
					{leadingIcon}
				</span>
			) : null}
			{isDisabledByLoading ? (
				<Skeleton
					className="h-16 min-w-0 flex-1"
					data-testid={setTestId('loading-skeleton')}
				/>
			) : (
				<div
					className={cn('min-w-0 flex-1', isClearControlVisible && 'pr-36')}
					data-testid={setTestId('dropdown-search-wrap')}
				>
					<SearchClearableSearchInput {...inputProps} />
				</div>
			)}
		</>
	);
}

function SearchClearableTotalItems({
	menuContentKind,
	totalItems,
	setTestId,
}: Readonly<{
	menuContentKind: ContentTypeToRender;
	totalItems: number | undefined;
	setTestId: (elementName: string) => string;
}>) {
	if (
		menuContentKind !== ContentTypeToRender.Ready ||
		totalItems === undefined
	) {
		return null;
	}

	return (
		<div
			className="flex justify-center py-4"
			data-testid={setTestId('dropdown-total-items')}
		>
			<BodyS className="text-text-secondary">{totalItems} items in total</BodyS>
		</div>
	);
}

function SearchClearableClearControl({
	isVisible,
	onClear,
}: Readonly<{ isVisible: boolean; onClear: () => void }>) {
	if (!isVisible) {
		return null;
	}

	return (
		<div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center">
			<Button
				type="button"
				variant="link"
				className="pointer-events-auto h-36 w-36 shrink-0 rounded-none border-0 px-0 focus-visible:outline-none"
				data-testid="erase-option-button"
				aria-label="Clear selection"
				onClick={(event) => {
					event.stopPropagation();
					onClear();
				}}
			>
				<Icon name="x" size="xs" />
			</Button>
		</div>
	);
}

function SearchClearableSelectDropdown<T extends IDropdownItem>({
	items,
	selectedItem,
	onSelect,
	triggerClassName,
	contentClassName,
	hasSameWidth = false,
	align = 'start',
	placeholder,
	hasGreyPlaceholder = false,
	TEST_ID,
	onOpen,
	isLoadingOptions,
	isVirtualized = false,
	virtualListMaxHeight = 240,
	virtualListRowHeight = 40,
	leadingIcon,
	menuPortalContainer,
	onSearchTermChange,
	searchDebounceMs = 500,
	totalItems,
	loadingSpinner,
}: Readonly<ISearchClearableSelectDropdownProps<T>>) {
	const anchorRef = useRef<HTMLDivElement>(null);
	const isServerSearch = Boolean(onSearchTermChange);
	const isSpinnerLoading = loadingSpinner != null;
	const isDisabledByLoading =
		isSpinnerLoading || (Boolean(isLoadingOptions) && !isServerSearch);

	const { isOpen, filterQuery, setFilterQuery, handleOpenChange } =
		useSearchClearableOpenState(
			isDisabledByLoading,
			isServerSearch,
			onOpen,
			selectedItem?.name
		);

	const setTestId = makeDataTestId(TEST_ID);

	useDebouncedSearchTerm(
		filterQuery,
		isServerSearch,
		searchDebounceMs,
		onSearchTermChange
	);

	const displayedItems = useMemo(
		() => (isServerSearch ? items : filterItemsByQuery(items, filterQuery)),
		[items, filterQuery, isServerSearch]
	);

	const virtualListHeight = getVirtualListHeight(
		isVirtualized,
		displayedItems.length,
		virtualListMaxHeight,
		virtualListRowHeight
	);

	const menuContentKind = getMenuContentKind(
		isLoadingOptions,
		isServerSearch,
		items.length,
		displayedItems.length
	);
	const isClearControlVisible = !isDisabledByLoading && selectedItem != null;

	const openMenu = () => {
		if (!isOpen) {
			handleOpenChange(true);
		}
	};

	const handlePickItem = (item: T) => {
		onSelect(item);
		handleOpenChange(false);
	};

	const resolvedPlaceholder = placeholder ?? 'Start typing to select';
	const listPanelId = `${TEST_ID}-dropdown-menu-list`;

	const virtualListItemData: ISearchClearableVirtualListRowData<T> = {
		displayedItems,
		selectedItem,
		setTestId,
		onPick: handlePickItem,
	};

	return (
		<Popover
			modal={false}
			open={isDisabledByLoading ? false : isOpen}
			onOpenChange={handleOpenChange}
		>
			<div
				className={cn(
					'flex min-w-0 max-w-full items-stretch overflow-hidden rounded-m border border-zinc-200 bg-white text-text-primary shadow-sm',
					'hover:not-focus-within:bg-base-2',
					'outline-none focus-within:ring-2 focus-within:ring-interactive focus-within:ring-offset-0',
					isDisabledByLoading &&
						'pointer-events-none cursor-not-allowed hover:bg-white',
					triggerClassName
				)}
				data-testid={setTestId('dropdown')}
				aria-busy={isLoadingOptions || undefined}
			>
				<div className="relative min-w-0 flex-1">
					{/* Anchor/SearchBox stay inline — PopoverAnchor asChild needs this div. */}
					<PopoverAnchor asChild>
						<div
							ref={anchorRef}
							className={cn(
								'flex h-36 w-full min-w-0 items-center gap-8 rounded-none border-0 bg-transparent',
								isDisabledByLoading ? 'cursor-not-allowed' : 'cursor-text',
								isSpinnerLoading && 'justify-center',
								'outline-none focus-visible:outline-none'
							)}
							data-testid={setTestId('dropdown-menu-trigger')}
						>
							<SearchClearableTriggerContent
								isDisabledByLoading={isDisabledByLoading}
								isSpinnerLoading={isSpinnerLoading}
								isClearControlVisible={isClearControlVisible}
								leadingIcon={leadingIcon}
								loadingSpinner={loadingSpinner}
								isOpen={isOpen}
								filterQuery={filterQuery}
								selectedItemName={selectedItem?.name}
								placeholder={resolvedPlaceholder}
								hasGreyPlaceholder={hasGreyPlaceholder}
								listPanelId={listPanelId}
								setTestId={setTestId}
								onOpenMenu={openMenu}
								onFilterQueryChange={setFilterQuery}
							/>
						</div>
					</PopoverAnchor>

					<PopoverContent
						id={listPanelId}
						portalContainer={menuPortalContainer}
						align={align}
						sideOffset={4}
						role="listbox"
						data-testid={setTestId('dropdown-menu-list')}
						onOpenAutoFocus={(event) => event.preventDefault()}
						onCloseAutoFocus={(event) => event.preventDefault()}
						onFocusOutside={(event) => event.preventDefault()}
						onPointerDownOutside={(event) =>
							preventPopoverCloseFromAnchor(event, anchorRef.current)
						}
						onInteractOutside={(event) =>
							preventPopoverCloseFromAnchor(event, anchorRef.current)
						}
						className={cn(
							'z-[100] w-auto rounded-m border-divider p-0 shadow-lg outline-hidden',
							hasSameWidth
								? 'w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)]'
								: 'min-w-200',
							!isVirtualized &&
								'max-h-[min(280px,var(--radix-popover-content-available-height,280px))] overflow-y-auto',
							contentClassName
						)}
					>
						<SearchClearableMenuBody
							menuContentKind={menuContentKind}
							isVirtualized={isVirtualized}
							displayedItems={displayedItems}
							selectedItem={selectedItem}
							onPick={handlePickItem}
							setTestId={setTestId}
							virtualListHeight={virtualListHeight}
							virtualListRowHeight={virtualListRowHeight}
							virtualListItemData={virtualListItemData}
						/>
						<SearchClearableTotalItems
							menuContentKind={menuContentKind}
							totalItems={totalItems}
							setTestId={setTestId}
						/>
					</PopoverContent>

					<SearchClearableClearControl
						isVisible={isClearControlVisible}
						onClear={() => {
							onSelect(null);
							handleOpenChange(false);
						}}
					/>
				</div>
			</div>
		</Popover>
	);
}

export { SearchClearableSelectDropdown };
